import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, User, LogOut, LayoutDashboard, Settings, BarChart3 } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import NotificationBell from './NotificationBell';
// Language selector, NOT mounted. It switched i18next's language and nothing else: no page calls
// `t()`, so every string on the site is hardcoded English and the menu changed nothing visible.
// Offering 13 languages that do nothing is worse than offering none on a site people are deciding
// whether to trust with their financial details. The component and `src/i18n/locales/*` are kept
// so it can be mounted again once the pages actually read from them.
// import LanguageSelector from './LanguageSelector';

const API = `${process.env.REACT_APP_BACKEND_URL || '/_/backend'}/api`;

// Pages that have dark hero sections where navbar should have white text
const DARK_HERO_PAGES = ['/', '/get-started'];

export const Navbar = () => {
  // Initialise from actual scroll position so hard-reload-at-scroll is correct.
  const [isScrolled, setIsScrolled] = useState(() => window.scrollY > 50);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Check if current page has a dark hero
  const hasDarkHero = DARK_HERO_PAGES.includes(location.pathname);
  
  // Determine if navbar should use light (white text) or dark (black text) styling
  const useLightStyle = hasDarkHero && !isScrolled;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    
    // On dark-hero pages always start unscrolled so white text is visible
    // immediately — scroll position is not reset by React Router on navigation,
    // so we can't rely on window.scrollY being 0 when arriving at these pages.
    setIsScrolled(hasDarkHero ? false : window.scrollY > 50);
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasDarkHero, location.pathname]);

  useEffect(() => {
    // Close mobile menu on route change
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const res = await fetch(`${API}/auth/me`, { credentials: 'include' });
        if (res.ok) {
          const userData = await res.json();
          setUser(userData);
        }
      } catch (e) {
        // Not logged in
      }
    };
    checkAuth();
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/logout`, { method: 'POST', credentials: 'include' });
      setUser(null);
      navigate('/');
    } catch (e) {
      console.error('Logout error', e);
    }
  };

  const navLinks = [
    { href: '/calculator', label: 'Calculator' },
  ];

  // Logo URLs — Levr branding for both states
  const levrLogoLight = "/levr-logo.png";
  const levrLogoDark = "/levr-logo-dark.png";

  return (
    <nav 
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        useLightStyle ? 'bg-transparent' : 'navbar-glass shadow-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between h-20 md:h-24">
          {/* Logo - changes based on background */}
          <Link 
            to="/" 
            data-testid="logo-link"
            className="flex items-center"
          >
            <img
              src={useLightStyle ? levrLogoLight : levrLogoDark}
              alt="Levr"
              className="w-[6.8rem] md:w-[9.35rem] h-auto object-contain transition-all duration-300"
            />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`nav-${link.label.toLowerCase()}`}
                className={`text-sm font-medium transition-colors hover:text-[#00D47E] ${
                  useLightStyle 
                    ? 'text-white'
                    : (location.pathname === link.href ? 'text-black' : 'text-gray-600')
                }`}
              >
                {link.label}
              </Link>
            ))}
            
            {/* Language selector removed for launch — see the note on the import. */}
          </div>

          {/* Auth Buttons */}
          <div className="hidden md:flex items-center gap-4">
            {user ? (
              <>
                {/* Notification Bell */}
                <div className={useLightStyle ? 'text-white' : ''}>
                  <NotificationBell />
                </div>
                
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="outline" 
                      data-testid="user-menu-trigger"
                      className={`rounded-full px-4 transition-colors hover:text-[#00D47E] hover:border-[#00D47E] ${
                        useLightStyle 
                          ? 'border-white/50 text-white bg-transparent hover:bg-white/10' 
                          : 'border-gray-300 text-gray-700'
                      }`}
                    >
                      <User className="w-4 h-4 mr-2" />
                      {user.name?.split(' ')[0] || 'Account'}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="menu-dashboard">
                      <LayoutDashboard className="w-4 h-4 mr-2" />
                      Dashboard
                    </DropdownMenuItem>
                    {user.role === 'admin' && (
                      <>
                        <DropdownMenuItem onClick={() => navigate('/analytics')} data-testid="menu-analytics">
                          <BarChart3 className="w-4 h-4 mr-2" />
                          Analytics
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="menu-admin">
                          <Settings className="w-4 h-4 mr-2" />
                          Admin Panel
                        </DropdownMenuItem>
                      </>
                    )}
                    {user.role === 'dealer' && (
                      <DropdownMenuItem onClick={() => navigate('/dealer')} data-testid="menu-dealer">
                        <LayoutDashboard className="w-4 h-4 mr-2" />
                        Dealer Dashboard
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                      <LogOut className="w-4 h-4 mr-2" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button 
                    variant="ghost" 
                    data-testid="login-btn"
                    className={`transition-colors hover:text-[#00D47E] ${
                      useLightStyle ? 'text-white hover:bg-white/10' : 'text-gray-600'
                    }`}
                  >
                    Login
                  </Button>
                </Link>
                <Link to="/get-started">
                  <Button 
                    data-testid="get-started-btn"
                    className="bg-[#00D47E] text-black hover:bg-[#00D47E]/90 rounded-full px-6"
                  >
                    Get Started
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-toggle"
            className={`md:hidden p-2 transition-colors ${useLightStyle ? 'text-white' : 'text-gray-600'}`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-200 bg-white rounded-b-xl shadow-lg">
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                  className="px-4 py-2 text-gray-600 hover:text-black hover:bg-gray-50"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              
              {/* Language selector removed for launch — see the note on the import. */}

              <div className="px-4 pt-2 border-t border-gray-200 flex flex-col gap-2">
                {user ? (
                  <>
                    <Link to="/dashboard" onClick={() => setIsMobileMenuOpen(false)}>
                      <Button variant="outline" className="w-full justify-start" data-testid="mobile-dashboard">
                        <LayoutDashboard className="w-4 h-4 mr-2" />
                        Dashboard
                      </Button>
                    </Link>
                    {user.role === 'dealer' && (
                      <Link to="/dealer" onClick={() => setIsMobileMenuOpen(false)}>
                        <Button variant="outline" className="w-full justify-start" data-testid="mobile-dealer">
                          <LayoutDashboard className="w-4 h-4 mr-2" />
                          Dealer Dashboard
                        </Button>
                      </Link>
                    )}
                    <Button variant="ghost" className="w-full justify-start text-red-600" onClick={handleLogout} data-testid="mobile-logout">
                      <LogOut className="w-4 h-4 mr-2" />
                      Logout
                    </Button>
                  </>
                ) : (
                  <>
                    <Link to="/login" onClick={() => setIsMobileMenuOpen(false)}>
                      <Button variant="outline" className="w-full" data-testid="mobile-login">Login</Button>
                    </Link>
                    <Link to="/get-started" onClick={() => setIsMobileMenuOpen(false)}>
                      <Button className="w-full bg-black text-white" data-testid="mobile-get-started">Get Started</Button>
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
